import React, { useCallback, useEffect, useState } from 'react';
import { consumerApi } from 'lib/consumer-api';
import { API_ENDPOINTS } from 'constants/endpoints';
import { Gamepad2, ExternalLink, Search } from 'lucide-react';
import ConsumerListPageLayout from 'components/consumer/ConsumerListPageLayout';
import {
  ConsumerFilterField,
  consumerFilterInputClass,
  consumerFilterInputMonoClass,
} from 'components/consumer/ConsumerFilterBar';
import { CONSUMER_TD_CLASS, CONSUMER_TH_CLASS } from 'components/consumer/consumer-list-styles';
import { appendCursorParams, parseCursorPagination } from 'lib/cursor-pagination';
import { useCursorListPage } from 'hooks/useCursorListPage';
import type { ConsumerPortalGame } from 'types/consumer';

type GameFilters = { name: string; gameId: string; status: '' | 'active' | 'inactive' };
const emptyFilters: GameFilters = { name: '', gameId: '', status: '' };
const countFilters = (f: GameFilters) =>
  (f.name.trim() ? 1 : 0) + (f.gameId.trim() ? 1 : 0) + (f.status ? 1 : 0);

function buildFilterParams(filters: GameFilters) {
  const params = new URLSearchParams();
  if (filters.name.trim()) params.set('name', filters.name.trim());
  if (filters.gameId.trim()) params.set('gameId', filters.gameId.trim());
  if (filters.status) params.set('status', filters.status);
  return params;
}

const ConsumerGames = () => {
  const [draftFilters, setDraftFilters] = useState(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);

  const fetchPage = useCallback(async (cursor: number | null) => {
    const params = appendCursorParams(buildFilterParams(appliedFilters), cursor);
    const res = await consumerApi.get<ConsumerPortalGame[]>(
      `${API_ENDPOINTS.CONSUMER_PORTAL.GAMES}?${params.toString()}`
    );
    const meta = parseCursorPagination(res);
    return {
      items: res.success && Array.isArray(res.data) ? res.data : [],
      nextCursor: meta.nextCursor,
      hasMore: meta.hasMore,
      total: meta.total,
      totalPages: meta.totalPages,
    };
  }, [appliedFilters]);

  const {
    page, items, loading, refreshing, total, totalPages, limit,
    resetAndLoad, refreshCurrent, goFirst, goToPage, goLast,
  } = useCursorListPage(fetchPage);
  useEffect(() => { resetAndLoad(); }, [appliedFilters, resetAndLoad]);

  const isDatasetEmpty = total === 0 && !loading && !countFilters(appliedFilters);
  const isFilteredEmpty = items.length === 0 && !loading && countFilters(appliedFilters) > 0;

  return (
    <ConsumerListPageLayout
      icon={Gamepad2}
      title="Games"
      description="Assigned games are read-only. Contact platform admin to change assignments."
      totalCount={null}
      filteredCount={items.length}
      loading={loading}
      refreshing={refreshing}
      onRefresh={() => refreshCurrent()}
      activeFilterCount={countFilters(appliedFilters)}
      draftFilterCount={countFilters(draftFilters)}
      onApplyFilters={() => setAppliedFilters({ ...draftFilters })}
      onClearFilters={() => { setDraftFilters(emptyFilters); setAppliedFilters(emptyFilters); }}
      filterFields={
        <>
          <ConsumerFilterField label="Game name">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" size={15} />
              <input type="text" placeholder="Search name" value={draftFilters.name}
                onChange={(e) => setDraftFilters((p) => ({ ...p, name: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && setAppliedFilters({ ...draftFilters })}
                className={`${consumerFilterInputClass} pl-9`} />
            </div>
          </ConsumerFilterField>
          <ConsumerFilterField label="Game ID">
            <input type="text" placeholder="e.g. teen-patti" value={draftFilters.gameId}
              onChange={(e) => setDraftFilters((p) => ({ ...p, gameId: e.target.value }))}
              className={consumerFilterInputMonoClass} />
          </ConsumerFilterField>
          <ConsumerFilterField label="Status">
            <select value={draftFilters.status}
              onChange={(e) => setDraftFilters((p) => ({ ...p, status: e.target.value as GameFilters['status'] }))}
              className={consumerFilterInputClass}>
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </ConsumerFilterField>
        </>
      }
      tableTitle="Assigned Games"
      tableSubtitle="Platform games linked to your consumer"
      emptyIcon={Gamepad2}
      emptyTitle="No games assigned"
      emptyDescription="Your consumer has no games linked yet."
      isDatasetEmpty={isDatasetEmpty}
      isFilteredEmpty={isFilteredEmpty}
      colSpan={6}
      page={page}
      itemCount={items.length}
      limit={limit}
      total={total}
      totalPages={totalPages}
      onFirst={goFirst}
      onPageChange={goToPage}
      onLast={goLast}
      tableHead={
        <tr>
          <th className={CONSUMER_TH_CLASS}>Game</th>
          <th className={CONSUMER_TH_CLASS}>Game ID</th>
          <th className={CONSUMER_TH_CLASS}>Status</th>
          <th className={`${CONSUMER_TH_CLASS} min-w-[180px]`}>Frontend</th>
          <th className={`${CONSUMER_TH_CLASS} min-w-[180px]`}>Backend</th>
          <th className={CONSUMER_TH_CLASS}>Access</th>
        </tr>
      }
      tableBody={
        <>
          {items.map((g) => (
            <tr key={g.id} className="hover:bg-subtle/40 transition-colors">
              <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}><span className="text-sm font-semibold text-heading">{g.name}</span></td>
              <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}><span className="font-mono text-xs font-bold text-brand-gold">{g.gameId}</span></td>
              <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}>
                <span className={`px-2 py-0.5 text-[10px] font-bold rounded-md uppercase ${g.status ? 'bg-emerald-500/10 text-emerald-700' : 'bg-red-500/10 text-red-700'}`}>
                  {g.status ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td className={`${CONSUMER_TD_CLASS} max-w-[220px]`}>
                <a href={g.frontendUrl} target="_blank" rel="noopener noreferrer" className="inline-flex max-w-full items-center gap-1.5 text-xs font-mono text-muted hover:text-heading truncate">
                  <span className="truncate">{g.frontendUrl}</span><ExternalLink size={12} className="shrink-0" />
                </a>
              </td>
              <td className={`${CONSUMER_TD_CLASS} max-w-[220px]`}>
                <a href={g.backendUrl} target="_blank" rel="noopener noreferrer" className="inline-flex max-w-full items-center gap-1.5 text-xs font-mono text-muted hover:text-heading truncate">
                  <span className="truncate">{g.backendUrl}</span><ExternalLink size={12} className="shrink-0" />
                </a>
              </td>
              <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}>
                <span className="px-2 py-0.5 text-[10px] font-bold rounded-md uppercase bg-amber-500/10 text-amber-700">Read-only</span>
              </td>
            </tr>
          ))}
        </>
      }
    />
  );
};

export default ConsumerGames;
